import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import CTA from "@/components/CTA";
import { PROJECTS } from "@/data/content";

export default function ProjectDetail() {
  const { projectId } = useParams();
  const index = PROJECTS.findIndex((p) => p.id === projectId);
  if (index === -1) return <Navigate to="/portfolio" replace />;
  const project = PROJECTS[index];
  const next = PROJECTS[(index + 1) % PROJECTS.length];

  return (
    <>
      <section className="relative min-h-[68vh] flex flex-col justify-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/20 to-void" />
        </div>
        <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 w-full pt-36 pb-12">
          <Reveal>
            <Link
              to="/portfolio"
              data-testid="project-detail-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> All projects
            </Link>
          </Reveal>
          <p className="mt-8 text-xs font-mono uppercase tracking-[0.3em] text-[#C5A059]">{project.category}</p>
          <h1
            data-testid="project-detail-title"
            className="mt-4 font-display font-extrabold uppercase tracking-tight leading-[1.02] text-zinc-50 text-3xl sm:text-5xl lg:text-6xl max-w-4xl"
          >
            <MaskedLine delay={0.15}>{project.title}</MaskedLine>
          </h1>
          <Reveal delay={0.4}>
            <div
              data-testid="project-detail-meta"
              className="mt-10 flex flex-wrap gap-x-10 gap-y-4 border-t border-zinc-700/60 pt-6 font-mono"
            >
              {[
                ["Client", project.client],
                ["Location", project.location],
                ["Value", project.value],
                ["Status", project.status],
                ["Date", project.date],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
                  <p className="mt-1.5 text-xs text-zinc-100">{value}</p>
                </div>
              ))}
            </div>
          </Reveal>
          {project.id !== "fountainbridge-masterplan" && (
            <Reveal delay={0.5}>
              <p
                data-testid="project-detail-note"
                className="mt-6 inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-400 border border-zinc-700/80 px-4 py-2.5"
              >
                Director experience — delivered with a previous employer
              </p>
            </Reveal>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-3 gap-14">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
              <span className="w-10 h-px bg-[#C5A059]" /> The scheme
            </p>
            <p className="mt-6 font-display text-xl sm:text-2xl font-bold text-zinc-100 leading-snug">
              {project.summary}
            </p>
            <p className="mt-6 text-base text-zinc-300 font-light leading-relaxed">{project.body}</p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="border border-zinc-800/80 bg-void-2/30 p-8 h-fit">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-6">Key facts</p>
            <ul className="space-y-4">
              {project.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm text-zinc-300 font-light">
                  <Check className="w-4 h-4 mt-0.5 text-[#C5A059] shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="project-detail-gallery">
        <div className={`grid gap-6 ${project.gallery.length > 1 ? "sm:grid-cols-2" : ""}`}>
          {project.gallery.map((src, i) => (
            <Reveal key={src} delay={i * 0.12}>
              <div className="overflow-hidden border border-zinc-800/80">
                <img
                  src={src}
                  alt={`${project.title} — image ${i + 2}`}
                  className="w-full h-72 sm:h-96 object-cover hover:scale-[1.03] transition-transform duration-700 ease-out"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-zinc-800/60">
        <Link
          to={`/portfolio/${next.id}`}
          data-testid="project-detail-next-link"
          className="group block max-w-7xl mx-auto px-6 py-16 sm:py-20"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Next project</p>
          <div className="mt-4 flex items-center justify-between gap-6">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 group-hover:text-[#C5A059] transition-colors duration-300">
              {next.title}
            </h2>
            <ArrowRight className="w-8 h-8 text-zinc-500 group-hover:text-[#C5A059] group-hover:translate-x-2 transition-all duration-300 shrink-0" />
          </div>
        </Link>
      </section>

      <CTA title="Planning something similar?" text="Talk to us about how we can bring the same certainty to your project." />
    </>
  );
}
